import { useChat } from "@ai-sdk/react";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { fetchMessages } from "../services/chatApi";
import { createChatTransport } from "../services/chatTransport";
import { mapServerMessagesToUI } from "../utils/messageMapper";
import type { UseKoachChatReturn } from "../types";

// Create transport once outside component to avoid re-creation on each render
const chatTransport = createChatTransport();

export function useKoachChat(): UseKoachChatReturn {
  const {
    id,
    messages,
    sendMessage: send,
    setMessages,
    stop,
    status,
    error,
  } = useChat({
    transport: chatTransport,
  });

  // History loading state
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historyError, setHistoryError] = useState<Error | null>(null);
  const nextCursorRef = useRef<string | null>(null);
  const initialLoadDone = useRef(false);

  // Load initial history
  const loadInitialHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      setHistoryError(null);
      const response = await fetchMessages(id);
      const uiMessages = mapServerMessagesToUI(response.messages);
      setMessages(uiMessages);
      nextCursorRef.current = response.nextCursor ?? null;
      setHasMoreHistory(response.nextCursor != null);
    } catch (err) {
      console.error("Failed to load chat history:", err);
      setHistoryError(err instanceof Error ? err : new Error("Failed to load chat history"));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [id, setMessages]);

  // Load initial history on mount
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    loadInitialHistory();
  }, [loadInitialHistory]);

  // Load more history (for infinite scroll)
  const loadMoreHistory = useCallback(async () => {
    if (!hasMoreHistory || isLoadingHistory || !nextCursorRef.current) return;

    try {
      setIsLoadingHistory(true);
      setHistoryError(null);
      const response = await fetchMessages(id, nextCursorRef.current);
      const olderMessages = mapServerMessagesToUI(response.messages);

      // Prepend older messages to existing messages
      setMessages((prev) => [...olderMessages, ...prev]);

      nextCursorRef.current = response.nextCursor ?? null;
      setHasMoreHistory(response.nextCursor != null);
    } catch (err) {
      console.error("Failed to load more history:", err);
      setHistoryError(err instanceof Error ? err : new Error("Failed to load more history"));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [id, hasMoreHistory, isLoadingHistory, setMessages]);

  // Retry loading history (for error recovery)
  const retryLoadHistory = useCallback(async () => {
    if (messages.length === 0) {
      // Retry initial load
      initialLoadDone.current = false;
      await loadInitialHistory();
    } else {
      // Retry loading more
      await loadMoreHistory();
    }
  }, [messages.length, loadInitialHistory, loadMoreHistory]);

  const derivedState = useMemo(() => {
    const isStreaming = status === "streaming";
    const isThinking = status === "submitted" || isStreaming;
    const canSend = status === "ready" && !error;

    return { isThinking, isStreaming, canSend };
  }, [status, error]);

  const sendMessage = (text: string) => {
    if (!derivedState.canSend || !text.trim()) return;
    send({ parts: [{ type: "text", text }] });
  };

  return {
    id,
    messages,
    sendMessage,
    stop,
    status,
    error: error ?? null,
    ...derivedState,
    isLoadingHistory,
    hasMoreHistory,
    historyError,
    loadMoreHistory,
    retryLoadHistory,
  };
}
